import React from 'react';
import { AlertTriangle, CheckCircle, GitMerge } from 'lucide-react';
import type { CrossValidationResult } from '../services/patternCrossValidationService';

const PATTERN_LABELS: Record<string, string> = {
  axonal: 'Axonal',
  demyelinating: 'Desmielinizante',
  neuropathic: 'Neuropático',
  myopathic: 'Miopático',
  motor_neuron_disease: 'Enfermedad motoneurona',
};

interface PatternConsistencyPanelProps {
  result: CrossValidationResult | null;
  className?: string;
}

const PatternConsistencyPanel: React.FC<PatternConsistencyPanelProps> = ({
  result,
  className = '',
}) => {
  if (!result) {
    return (
      <p className={`text-sm text-gray-400 ${className}`}>
        Validación cruzada NCS/EMG no aplicable (EMG no realizado o sin datos).
      </p>
    );
  }

  const { conflicts, finalDiagnosis, detectedPatterns, recommendations, isConsistent } = result;

  return (
    <div className={`space-y-4 ${className}`}>
      {conflicts.length === 0 ? (
        <div className="bg-green-900/30 border border-green-700/50 p-4 rounded-lg flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-green-300 text-sm font-medium">
              Los patrones electrofisiológicos NCS/EMG son consistentes entre sí.
            </p>
            {finalDiagnosis.clinicalCorrelation && (
              <p className="text-green-200/80 text-xs mt-1">{finalDiagnosis.clinicalCorrelation}</p>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {conflicts.map((conflict, index) => (
            <div key={index} className="bg-red-900/30 border border-red-700/50 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-1" />
                <div className="min-w-0">
                  <h5 className="font-semibold text-red-300 text-sm">
                    {conflict.conflictType === 'incompatible'
                      ? 'Incompatibilidad entre patrones'
                      : 'Aclaración requerida'}
                    {' — '}
                    {PATTERN_LABELS[conflict.pattern1] ?? conflict.pattern1} vs{' '}
                    {PATTERN_LABELS[conflict.pattern2] ?? conflict.pattern2}
                  </h5>
                  <p className="text-sm text-red-200 mt-1">{conflict.description}</p>
                  {conflict.possibleExplanations.length > 0 && (
                    <div className="mt-3">
                      <h6 className="text-xs font-bold text-gray-300 uppercase">
                        Posibles explicaciones
                      </h6>
                      <ul className="list-disc pl-5 mt-1 text-xs text-gray-400 space-y-1">
                        {conflict.possibleExplanations.map((explanation, i) => (
                          <li key={i}>{explanation}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {conflict.recommendedActions.length > 0 && (
                    <div className="mt-3">
                      <h6 className="text-xs font-bold text-gray-300 uppercase">
                        Acciones recomendadas
                      </h6>
                      <ul className="list-disc pl-5 mt-1 text-xs text-gray-400 space-y-1">
                        {conflict.recommendedActions.map((action, i) => (
                          <li key={i}>{action}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {detectedPatterns.length > 0 && (
        <div className="bg-gray-800/60 border border-gray-600 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <GitMerge className="h-4 w-4 text-blue-400" />
            <h6 className="text-sm font-semibold text-gray-200">Integración NCS/EMG</h6>
          </div>
          <p className="text-xs text-gray-400 mb-2">
            Tipo: <span className="text-gray-200">{finalDiagnosis.diagnosisType}</span>
            {' · '}
            Confianza integrada: {(finalDiagnosis.confidence * 100).toFixed(0)}%
            {!isConsistent && ' · Revisar conflictos'}
          </p>
          <ul className="text-xs text-gray-300 space-y-1">
            {detectedPatterns.slice(0, 4).map((pattern) => (
              <li key={pattern.patternType}>
                {PATTERN_LABELS[pattern.patternType] ?? pattern.patternType} — score{' '}
                {(pattern.score * 100).toFixed(0)}%
              </li>
            ))}
          </ul>
        </div>
      )}

      {recommendations.length > 0 && (
        <div className="bg-blue-900/20 border border-blue-700/40 rounded-lg p-4">
          <h6 className="text-xs font-bold text-blue-300 uppercase mb-2">
            Recomendaciones de validación cruzada
          </h6>
          <ul className="list-disc pl-5 text-xs text-blue-100 space-y-1">
            {recommendations.map((rec, i) => (
              <li key={i}>{rec}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PatternConsistencyPanel;
